import React, { useState, useEffect, useRef } from "react";
import { X, Edit, Trash2, User, UserPlus } from "lucide-react";
import CustomSelect from "./common/CustomSelect";
import DeleteConfirmationModal from "./members/DeleteMemberModal";
import {
  createLeadershipRole,
  updateLeadershipRole,
  deleteLeadershipRole,
} from "../../services/leadershipRoleServices";
import toast from "react-hot-toast";

const MemberRoleModal = ({
  isOpen,
  member,
  onClose,
  onMemberUpdate,
  designationList,
  refetch,
  getInitials,
}) => {
  // Form state
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedFromYear, setSelectedFromYear] = useState(null);
  const [selectedToYear, setSelectedToYear] = useState(null);
  const [isCurrent, setIsCurrent] = useState(false);

  // Component state
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [currentMember, setCurrentMember] = useState(member);
  const [loading, setLoading] = useState(false);
  const [memberRoles, setMemberRoles] = useState([]);
  const [leadershipHistoryId, setLeadershipHistoryId] = useState(null);

  // Ref for scrolling to form
  const roleFormRef = useRef(null);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    roleId: null,
    roleName: "",
    period: "",
    role: null,
  });

  // Initialize member and process roles data
  useEffect(() => {
    setCurrentMember(member);
    if (member?.roles && Array.isArray(member.roles)) {
      processLeadershipRoles(member.roles);
    }
  }, [member, designationList]);

  // Process leadership roles from member data - All Records Version
  const processLeadershipRoles = (leadershipRoles) => {
    try {
      // Early return for empty or invalid data
      if (
        !leadershipRoles ||
        !Array.isArray(leadershipRoles) ||
        leadershipRoles.length === 0
      ) {
        setMemberRoles([]);
        setLeadershipHistoryId(null);
        return;
      }

      console.log(`Processing ${leadershipRoles.length} leadership record(s)`);

      // Collect all roles from all leadership records
      const allMappedRoles = [];
      let primaryLeadershipId = null;

      leadershipRoles.forEach((leadershipRecord, index) => {
        // Validate record
        if (!leadershipRecord || !leadershipRecord._id) {
          console.warn(`Invalid leadership record at index ${index}`);
          return;
        }

        // Use the first valid record's ID as primary
        if (!primaryLeadershipId) {
          primaryLeadershipId = leadershipRecord._id;
        }

        const roles = Array.isArray(leadershipRecord.roles)
          ? leadershipRecord.roles
          : [];

        const mappedRoles = roles
          .filter((role) => role && role._id) // Filter out invalid roles
          .map((role) => {
            const roleName =
              role.designation?.name ||
              designationList?.find((d) => d._id === role.designationId)
                ?.name ||
              "Unknown Role";

            return {
              id: role._id,
              roleId: role.designationId,
              role: roleName,
              from: role.from || "N/A",
              to: role.to || "N/A",
              period: `${role.from || "N/A"} - ${role.to || "N/A"}`,
              isCurrent: Boolean(role.isCurrent),
              designationId: role.designationId,
              leadershipRecordId: leadershipRecord._id, // Track source record
            };
          });

        allMappedRoles.push(...mappedRoles);
      });

      // Sort all roles by from year (most recent first)
      allMappedRoles.sort((a, b) => {
        const yearA = typeof a.from === "number" ? a.from : 0;
        const yearB = typeof b.from === "number" ? b.from : 0;
        return yearB - yearA;
      });

      console.log(
        `Processed ${allMappedRoles.length} total roles from ${leadershipRoles.length} records`
      );

      setLeadershipHistoryId(primaryLeadershipId);
      setMemberRoles(allMappedRoles);
    } catch (error) {
      console.error("Error processing leadership roles:", error);
      toast.error("Error loading leadership roles. Please try again.");
      setMemberRoles([]);
      setLeadershipHistoryId(null);
    }
  };

  // Enhanced refetch function that updates modal state
  const refetchAndUpdateModal = async () => {
    try {
      if (refetch) {
        // Refetch the main data
        const result = await refetch();

        // Find the updated member data
        if (result?.data?.data?.members) {
          const updatedMember = result.data.data.members.find(
            (m) => m._id === currentMember.id
          );

          if (updatedMember) {
            // Transform the updated member data
            const transformedMember = {
              id: updatedMember._id,
              name: updatedMember.fullName,
              email: updatedMember.email,
              avatar: getInitials ? getInitials(updatedMember.fullName) : "??",
              membershipId: updatedMember.membershipId,
              mobile: updatedMember.mobile,
              status: updatedMember.status,
              membershipType: updatedMember.membershipType,
              createdAt: updatedMember.createdAt,
              isEmailVerified: updatedMember.isEmailVerified,
              isMobileVerified: updatedMember.isMobileVerified,
              roles: updatedMember.leadershipRoles || [],
            };

            // Update modal state with new data
            setCurrentMember(transformedMember);

            // Process the updated roles
            if (
              transformedMember.roles &&
              Array.isArray(transformedMember.roles)
            ) {
              processLeadershipRoles(transformedMember.roles);
            }

            // Update parent component if callback provided
            if (onMemberUpdate) {
              onMemberUpdate(transformedMember);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error refetching data:", error);
      throw error;
    }
  };

  // Generate options for dropdowns
  const roleOptions =
    designationList?.map((designation) => ({
      value: designation._id,
      label: designation.name,
    })) || [];

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2000; year <= currentYear + 5; year++) {
      years.push({ value: year.toString(), label: year.toString() });
    }
    return years.reverse();
  };

  const yearOptions = generateYearOptions();

  // Form handlers with enhanced refetch
  const handleSave = async () => {
    if (
      !selectedRole ||
      !selectedFromYear ||
      !selectedToYear ||
      !currentMember
    ) {
      toast.error("Please select role, from year, and to year before saving.");
      return;
    }

    if (parseInt(selectedFromYear.value) > parseInt(selectedToYear.value)) {
      toast.error("From year cannot be greater than To year");
      return;
    }

    try {
      setLoading(true);

      const roleData = {
        memberId: currentMember.id,
        designationId: selectedRole.value,
        from: parseInt(selectedFromYear.value),
        to: parseInt(selectedToYear.value),
        isCurrent: isCurrent,
      };

      if (editingRoleId) {
        // Update existing role
        if (!leadershipHistoryId) {
          throw new Error("Leadership history ID not found");
        }

        await updateLeadershipRole(
          leadershipHistoryId,
          editingRoleId,
          roleData
        );
        toast.success("Leadership role updated successfully!");
      } else {
        // Create new role
        await createLeadershipRole(roleData);
        toast.success("Leadership role added successfully!");
      }

      // Refetch and update modal state
      await refetchAndUpdateModal();

      clearForm();
    } catch (error) {
      console.error("Failed to save leadership role:", error);
      toast.error(
        error.message || "Failed to save leadership role. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role) => {
    const roleOption = roleOptions.find(
      (option) => option.value === role.designationId
    ) || { value: role.designationId, label: role.role };

    // Ensure years are handled as strings consistently
    const fromYear = role.from?.toString() || "";
    const toYear = role.to?.toString() || "";

    const fromYearOption = yearOptions.find(
      (option) => option.value === fromYear
    ) || { value: fromYear, label: fromYear };
    const toYearOption = yearOptions.find(
      (option) => option.value === toYear
    ) || { value: toYear, label: toYear };

    setSelectedRole(roleOption);
    setSelectedFromYear(fromYearOption);
    setSelectedToYear(toYearOption);
    setIsCurrent(role.isCurrent || false);
    setEditingRoleId(role.id);

    setTimeout(() => {
      roleFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const handleCancelEdit = () => {
    clearForm();
  };

  const clearForm = () => {
    setEditingRoleId(null);
    setSelectedRole(null);
    setSelectedFromYear(null);
    setSelectedToYear(null);
    setIsCurrent(false);
  };

  // Delete handlers with enhanced refetch
  const handleDeleteClick = (role) => {
    setDeleteModal({
      isOpen: true,
      roleId: role.id,
      roleName: role.role,
      period: role.period,
      role: role,
    });
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);

      if (!leadershipHistoryId) {
        throw new Error("Leadership history ID not found");
      }

      await deleteLeadershipRole(leadershipHistoryId, deleteModal.roleId);

      await refetchAndUpdateModal();

      toast.success("Leadership role deleted successfully!");
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Failed to delete leadership role:", error);
      toast.error(
        error.message || "Failed to delete leadership role. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      roleId: null,
      roleName: "",
      period: "",
      role: null,
    });
  };

  if (!isOpen || !currentMember) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[95vh] flex flex-col">
          <ModalHeader member={currentMember} onClose={onClose} />

          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              <MemberInfo member={currentMember} />

              <RoleForm
                ref={roleFormRef}
                selectedRole={selectedRole}
                setSelectedRole={setSelectedRole}
                selectedFromYear={selectedFromYear}
                setSelectedFromYear={setSelectedFromYear}
                selectedToYear={selectedToYear}
                setSelectedToYear={setSelectedToYear}
                isCurrent={isCurrent}
                setIsCurrent={setIsCurrent}
                roleOptions={roleOptions}
                yearOptions={yearOptions}
                editingRoleId={editingRoleId}
                onSave={handleSave}
                onCancelEdit={handleCancelEdit}
                loading={loading}
              />

              <RoleList
                member={currentMember}
                editingRoleId={editingRoleId}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                loading={loading}
                memberRoles={memberRoles}
              />
            </div>
          </div>

          <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onRequestClose={handleCloseDeleteModal}
        onDelete={handleConfirmDelete}
        itemName={`${deleteModal.roleName} (${deleteModal.period})`}
        itemType="leadership role"
      />
    </>
  );
};

// Sub-components
const ModalHeader = ({ member, onClose }) => (
  <div className="bg-[#4F22D8] text-white rounded-t-lg flex-shrink-0">
    <div className="flex items-center justify-between p-6">
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
          <UserPlus size={24} className="text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-white">
            Leadership Role Management
          </h2>
          <p className="text-blue-100 mt-1 text-sm">
            Managing roles for:{" "}
            <span className="font-medium text-white">{member?.name}</span>
          </p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="text-blue-100 hover:text-white transition-colors duration-200 p-1 hover:bg-white hover:bg-opacity-10 rounded-full"
      >
        <X size={24} />
      </button>
    </div>
  </div>
);

const MemberInfo = ({ member }) => (
  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6 border border-blue-100">
    <div className="flex items-center space-x-4">
      <div className="flex-1">
        <h3 className="text-xl font-semibold text-gray-900">{member?.name}</h3>
        <div className="flex flex-wrap gap-6 text-sm text-gray-600 mt-2">
          {member?.membershipId && (
            <span className="flex items-center">
              <span className="font-medium text-blue-700">ID:</span>
              <span className="ml-1">{member.membershipId}</span>
            </span>
          )}
          {member?.email && (
            <span className="flex items-center">
              <span className="font-medium text-blue-700">Email:</span>
              <span className="ml-1">{member.email}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
);

const RoleForm = React.forwardRef(
  (
    {
      selectedRole,
      setSelectedRole,
      selectedFromYear,
      setSelectedFromYear,
      selectedToYear,
      setSelectedToYear,
      isCurrent,
      setIsCurrent,
      roleOptions,
      yearOptions,
      editingRoleId,
      onSave,
      onCancelEdit,
      loading,
    },
    ref
  ) => {
    const isFormValid =
      selectedRole && selectedFromYear && selectedToYear && !loading;

    return (
      <div
        ref={ref}
        className="mb-8 bg-white border border-blue-100 rounded-lg p-6 shadow-sm"
      >
        <div className="flex items-center mb-6">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3">
            <UserPlus size={16} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">
            {editingRoleId ? "Update Role" : "Add New Role"}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <CustomSelect
            label="Leadership Role"
            value={selectedRole}
            onChange={setSelectedRole}
            options={roleOptions}
            placeholder="Select a role"
            required={true}
            isClearable={true}
          />

          <CustomSelect
            label="From Year"
            value={selectedFromYear}
            onChange={setSelectedFromYear}
            options={yearOptions}
            placeholder="Select from year"
            required={true}
            isClearable={true}
          />

          <CustomSelect
            label="To Year"
            value={selectedToYear}
            onChange={setSelectedToYear}
            options={yearOptions}
            placeholder="Select to year"
            required={true}
            isClearable={true}
          />
        </div>

        <div className="mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isCurrent}
              onChange={(e) => setIsCurrent(e.target.checked)}
              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Current Role
            </span>
          </label>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onSave}
            disabled={!isFormValid}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md ${
              isFormValid
                ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {loading ? "Saving..." : editingRoleId ? "Update Role" : "Add Role"}
          </button>

          {editingRoleId && (
            <button
              onClick={onCancelEdit}
              disabled={loading}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors duration-200 shadow-sm disabled:opacity-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }
);

// Add display name for debugging
RoleForm.displayName = "RoleForm";

const RoleList = ({
  member,
  editingRoleId,
  onEdit,
  onDelete,
  loading,
  memberRoles,
}) => (
  <div className="bg-white border border-blue-100 rounded-lg shadow-sm">
    <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
      <div className="flex items-center">
        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3">
          <User size={16} className="text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">
          Leadership Roles ({memberRoles?.length || 0})
        </h3>
      </div>
    </div>

    <div className="p-6">
      {loading ? (
        <LoadingState />
      ) : memberRoles && memberRoles.length > 0 ? (
        <RoleTable
          memberRoles={memberRoles}
          editingRoleId={editingRoleId}
          onEdit={onEdit}
          onDelete={onDelete}
          loading={loading}
        />
      ) : (
        <EmptyState />
      )}
    </div>
  </div>
);

const LoadingState = () => (
  <div className="text-center py-16">
    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    <p className="text-gray-600 mt-4">Loading roles...</p>
  </div>
);

const EmptyState = () => (
  <div className="text-center py-16">
    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <User size={32} className="text-blue-600" />
    </div>
    <p className="text-gray-600 mb-2 font-semibold text-lg">
      No roles assigned yet
    </p>
    <p className="text-sm text-gray-500">
      Use the form above to add a leadership role for this member
    </p>
  </div>
);

const RoleTable = ({
  memberRoles,
  editingRoleId,
  onEdit,
  onDelete,
  loading,
}) => (
  <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gradient-to-r from-blue-700 to-purple-700">
        <tr>
          <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
            S.No
          </th>
          <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
            Role
          </th>
          <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
            Period
          </th>
          <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
            Status
          </th>
          <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {memberRoles.map((role, index) => (
          <RoleRow
            key={role.id}
            role={role}
            index={index}
            editingRoleId={editingRoleId}
            onEdit={onEdit}
            onDelete={onDelete}
            loading={loading}
          />
        ))}
      </tbody>
    </table>
  </div>
);

const RoleRow = ({ role, index, editingRoleId, onEdit, onDelete, loading }) => (
  <tr
    className={`hover:bg-blue-50 transition-colors duration-150 ${
      editingRoleId === role.id ? "bg-blue-100 border-l-4 border-blue-500" : ""
    }`}
  >
    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
      {index + 1}
    </td>
    <td className="px-6 py-4 text-sm text-gray-900">
      <div className="font-semibold text-blue-700">
        {role.role || "Unknown Role"}
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      <span className="bg-gray-100 px-2 py-1 rounded-full text-xs font-medium">
        {role.period}
      </span>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm">
      {role.isCurrent ? (
        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
          Current
        </span>
      ) : (
        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium">
          Past
        </span>
      )}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm">
      <div className="flex items-center gap-3">
        <button
          onClick={() => onEdit(role)}
          disabled={loading}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors duration-200 hover:bg-blue-50 px-2 py-1 rounded disabled:opacity-50"
          title="Edit Role"
        >
          <Edit size={16} />
          <span className="hidden sm:inline font-medium">Edit</span>
        </button>
        <button
          onClick={() => onDelete(role)}
          disabled={loading}
          className="text-red-600 hover:text-red-800 flex items-center gap-1 transition-colors duration-200 hover:bg-red-50 px-2 py-1 rounded disabled:opacity-50"
          title="Delete Role"
        >
          <Trash2 size={16} />
          <span className="hidden sm:inline font-medium">Delete</span>
        </button>
      </div>
    </td>
  </tr>
);

export default MemberRoleModal;
